import { Col, Row } from 'antd';
import { useEffect, useState } from 'react';
import Select from 'react-select';

import { selectFormatDate } from 'app/commonRedux/appSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectPermissions } from 'features/auth/authSlice';
import { selectEmployee } from 'features/employee/employeeSlice';
import { useTranslation } from 'react-i18next';

import { DATE_FORMAT } from 'constants/commons';
import { PERMISSION_PERSONNEL_MANAGE } from 'constants/permissions';
import { REGEX_NUMBER } from 'constants/regex';

import { getProjectList } from 'features/overtime/overtimeAction';
import { selectOvertimeData } from 'features/overtime/overtimeSlice';
import CustomDatePicker from 'styles/datePickerStyled';
import CustomFormItem from 'styles/formStyled';
import CustomInput from 'styles/inputStyled';
import { selectConfiguration } from 'features/configuration/configurationSlice';

const EmployeeInformationFields = (props: any) => {
  const { isShowComponents, formData } = props;
  const { t } = useTranslation(['employee', 'overtime', 'payroll']);
  const dispatch = useAppDispatch();

  const dataFormatDate = useAppSelector(selectFormatDate) || DATE_FORMAT;
  const dataPermissions = useAppSelector(selectPermissions);
  const dataEmployee = useAppSelector(selectEmployee);
  const listProject = useAppSelector(selectOvertimeData).listProject;
  const dataConfiguration = useAppSelector(selectConfiguration);

  const [lineOptions, setLineOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [isFullEdit, setIsFullEdit] = useState(true);

  useEffect(() => {
    dispatch(getProjectList());
  }, [dispatch]);

  useEffect(() => {
    if (
      dataPermissions &&
      dataPermissions.includes(PERMISSION_PERSONNEL_MANAGE.PERSONNEL_UPDATE_RECORD)
    ) {
      setIsFullEdit(false);
    }
  }, [dataPermissions]);

  useEffect(() => {
    if (listProject.length > 0) {
      setLineOptions(
        listProject?.map((item: any) => {
          return {
            value: item.projectCode,
            label: item.name,
          };
        }),
      );
    }
    if (dataEmployee) {
      setManagerOptions(
        dataEmployee?.employeeListApproved?.map((item: any) => {
          return {
            value: item?.employeeId,
            label: `${item?.lastName} ${item?.firstName}`,
          };
        }),
      );
    }
  }, [listProject, dataEmployee]);

  return (
    <div style={isShowComponents ? {} : { display: 'none' }}>
      <Row gutter={[16, 16]}>
        <Col md={12} xs={24}>
          <CustomFormItem
            label={t('employee:employee_information_fields.work_email')}
            required={true}
            name="email"
            rules={[
              {
                required: true,
                message: `${t('validation:required', {
                  field: t('employee:employee_information_fields.work_email'),
                })}`,
              },
              {
                type: 'email',
                message: `${t('validation:is_valid', {
                  field: t('employee:employee_information_fields.work_email')
                    ?.toString()
                    ?.toLowerCase(),
                })}`,
              },
              {
                min: 6,
                message: `${t('validation:min', {
                  field: t('employee:employee_information_fields.work_email'),
                  min: '6',
                })}`,
              },
              {
                max: 64,
                message: `${t('validation:max', {
                  field: t('employee:employee_information_fields.work_email'),
                  max: '64',
                })}`,
              },
            ]}
          >
            <CustomInput
              disabled={isFullEdit}
              placeholder={t('employee:employee_information_fields.work_email_placeholder')}
            />
          </CustomFormItem>
        </Col>
        <Col md={12} xs={24}>
          <CustomFormItem
            label={t('overtime:project_name')}
            required={true}
            name="projectPrimary"
            rules={[
              {
                required: true,
                message: `${t('validation:required', {
                  field: t('overtime:project_name'),
                })}`,
              },
            ]}
          >
            <Select
              isDisabled={true}
              placeholder={t('payroll:contract_salary.line_category_placeholder')}
              maxMenuHeight={200}
              options={lineOptions}
              cacheOptions
              defaultOptions
            />
          </CustomFormItem>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col md={12} xs={24}>
          <CustomFormItem
            label={t('employee:employee_information_fields.direct_manager')}
            required={true}
            name="managers"
            rules={[
              {
                required: true,
                message: `${t('validation:required', {
                  field: t('employee:employee_information_fields.direct_manager'),
                })}`,
              },
            ]}
          >
            <Select
              isDisabled={isFullEdit}
              placeholder={t('employee:employee_information_fields.direct_manager_placeholder')}
              maxMenuHeight={200}
              options={managerOptions}
              cacheOptions
              defaultOptions
            />
          </CustomFormItem>
        </Col>
        <Col md={12} xs={24}>
          <CustomFormItem
            label={t('employee:employee_information_fields.tax_code')}
            required={false}
            name="taxCode"
            rules={[
              {
                required: false,
                message: `${t('validation:required', {
                  field: t('employee:employee_information_fields.tax_code'),
                })}`,
              },
              {
                pattern: new RegExp(REGEX_NUMBER),
                message: `${t('validation:is_valid', {
                  field: t('employee:employee_information_fields.tax_code')
                    ?.toString()
                    ?.toLowerCase(),
                })}`,
              },
            ]}
          >
            <CustomInput
              disabled={isFullEdit}
              placeholder={t('employee:employee_information_fields.tax_code_placeholder')}
            />
          </CustomFormItem>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col md={12} xs={24}>
          <CustomFormItem
            label={t('employee:employee_information_fields.employment_start_date')}
            required={false}
            name="startDate"
            rules={[
              {
                required: false,
                message: `${t('validation:required', {
                  field: t('employee:employee_information_fields.employment_start_date'),
                })}`,
              },
            ]}
          >
            <CustomDatePicker
              disabled={isFullEdit}
              placeholder={dataFormatDate.toString().toLowerCase()}
              format={dataFormatDate}
            />
          </CustomFormItem>
        </Col>
        <Col md={12} xs={24}>
          <CustomFormItem
            label={t('employee:employee_information_fields.social_insurance_number')}
            required={false}
            name="socialInsuranceNumber"
            rules={[
              {
                required: false,
                message: `${t('validation:required', {
                  field: t('employee:employee_information_fields.social_insurance_number'),
                })}`,
              },
              {
                pattern: new RegExp(REGEX_NUMBER),
                message: `${t('validation:is_valid', {
                  field: t('employee:employee_information_fields.social_insurance_number')
                    ?.toString()
                    ?.toLowerCase(),
                })}`,
              },
            ]}
          >
            <CustomInput
              disabled={isFullEdit}
              placeholder={t(
                'employee:employee_information_fields.social_insurance_number_placeholder',
              )}
            />
          </CustomFormItem>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col md={12} xs={24}>
          <CustomFormItem
            label={t('employee:employee_information_fields.location')}
            required={true}
            name="location"
            rules={[
              {
                // required: isRequriedCheckSendRequest || isRequriedCheckApprove,
                required: true,
                message: t('HRM_VAD_REQUIRED', {
                  field: t('employee:employee_information_fields.location'),
                }),
              },
            ]}
          >
            <Select
              placeholder={t('employee:employee_information_fields.location_placeholder')}
              maxMenuHeight={200}
              options={dataConfiguration?.dataLocation || []}
              cacheOptions
            />
          </CustomFormItem>
        </Col>
      </Row>
    </div>
  );
};

export default EmployeeInformationFields;
